import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CurrentLegalDoc {
  id: string;
  slug: 'b2b-contract' | 'dpa';
  version: string;
  effective_at: string;
}

export interface CompanyAcceptanceRow {
  id: string;
  contract_version: string;
  contract_document_id: string;
  dpa_version: string;
  dpa_document_id: string;
  representative_name: string | null;
  representative_role: string;
  accepted_at: string;
  accepted_by_user_id: string;
  ip_address: string | null;
  user_agent: string | null;
}

export interface CompanyContractStatus {
  hasAcceptance: boolean;
  needsReacceptance: boolean;
  currentContract: CurrentLegalDoc | null;
  currentDpa: CurrentLegalDoc | null;
  latestAcceptance: CompanyAcceptanceRow | null;
  acceptances: CompanyAcceptanceRow[];
}

/**
 * Returns the contract acceptance status for a given company.
 * - hasAcceptance: there's at least one record in company_contract_acceptances
 * - needsReacceptance: latest accepted versions don't match the current vigent versions
 */
export function useCompanyContractStatus(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['company-contract-status', companyId],
    enabled: !!companyId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CompanyContractStatus> => {
      if (!companyId) {
        return {
          hasAcceptance: false,
          needsReacceptance: false,
          currentContract: null,
          currentDpa: null,
          latestAcceptance: null,
          acceptances: [],
        };
      }

      const [{ data: docs }, { data: rows }] = await Promise.all([
        supabase
          .from('legal_documents')
          .select('id, slug, version, effective_at')
          .in('slug', ['b2b-contract', 'dpa'])
          .eq('locale', 'pt-BR')
          .is('superseded_at', null)
          .order('effective_at', { ascending: false }),
        supabase
          .from('company_contract_acceptances')
          .select(
            'id, contract_version, contract_document_id, dpa_version, dpa_document_id, representative_name, representative_role, accepted_at, accepted_by_user_id, ip_address, user_agent',
          )
          .eq('company_id', companyId)
          .order('accepted_at', { ascending: false }),
      ]);

      const docsList = (docs ?? []) as CurrentLegalDoc[];
      const currentContract = docsList.find((d) => d.slug === 'b2b-contract') ?? null;
      const currentDpa = docsList.find((d) => d.slug === 'dpa') ?? null;

      const acceptances = (rows ?? []) as unknown as CompanyAcceptanceRow[];
      const latestAcceptance = acceptances[0] ?? null;
      const hasAcceptance = !!latestAcceptance;

      const needsReacceptance =
        !hasAcceptance ||
        (currentContract && latestAcceptance?.contract_version !== currentContract.version) ||
        (currentDpa && latestAcceptance?.dpa_version !== currentDpa.version) ||
        false;

      return {
        hasAcceptance,
        needsReacceptance: !!needsReacceptance,
        currentContract,
        currentDpa,
        latestAcceptance,
        acceptances,
      };
    },
  });
}
